import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type { FilterDetailedText } from '../filter-detailed-text';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

export const BD_POSTAL_CODES_CONDITION_ID = 'postalCode';
const POSTAL_CODES_DETAIL_LIMIT = 10;
export const FilterConditionPostalCodesPluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionPostalCodesDto,
  BdPostalCodesCondition
> = {
  type: 'postalCodes',
  category: 'person',
  displayName: { de: 'Postleitzahlen' },
  sortingIndex: 5,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionPostalCodesDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionPostalCodesPlugin(obj.postalCodes);
  },
  fromCondition(
    jsonObj: BdPostalCodesCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionPostalCodesPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionPostalCodesPlugin([]);
  },
};

export class FilterConditionPostalCodesPlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionPostalCodesDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionPostalCodesDto,
    BdPostalCodesCondition
  > = FilterConditionPostalCodesPluginType;

  constructor(private postalCodes: string[]) {
    super(FilterConditionPostalCodesPluginType.type);
  }

  clone(): FilterConditionPlugin<string[]> {
    return new FilterConditionPostalCodesPlugin([...this.postalCodes]);
  }

  override equals(other: FilterConditionPostalCodesPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.postalCodes, other.postalCodes)
    );
  }

  override validationToText(): string {
    if (this.postalCodes.length === 0) {
      return 'Mindestens eine Postleitzahl auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdPostalCodesCondition(this.postalCodes, []);
  }

  toJSON(): FilterConditionPostalCodesDto | null {
    return {
      type: this.type,
      postalCodes: this.postalCodes,
    };
  }

  toText(): string | null {
    return this.postalCodes.length === 1
      ? 'eine Postleitzahl'
      : `${this.postalCodes.length} Postleitzahlen`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    if (this.postalCodes.length === 0) return { title };
    const shown = this.postalCodes.slice(0, POSTAL_CODES_DETAIL_LIMIT);
    const items = [...shown];
    const remaining = this.postalCodes.length - shown.length;
    if (remaining > 0) {
      items.push(`… und ${remaining} weitere Postleitzahlen`);
    }
    return { title, items };
  }

  toValue(): string[] {
    return this.postalCodes;
  }
}

export class BdPostalCodesCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(BD_POSTAL_CODES_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionPostalCodesDto {
  type: string;
  postalCodes: string[];
}
